<script setup lang="ts">
import { Plus, Trash2 } from 'lucide-vue-next'

import ReportRuleCondition from '@/components/reports/ReportRuleCondition.vue'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  type RegistryEntity,
  type RuleCondition,
  type RuleGroup,
  type RuleNode,
  emptyCondition,
  emptyGroup,
} from '@/types/reports'

defineOptions({ name: 'ReportRuleGroup' })

const props = defineProps<{
  group: RuleGroup
  entity: RegistryEntity
  isRoot?: boolean
  hideMatchHeader?: boolean
}>()

const emit = defineEmits<{
  'update:group': [RuleGroup]
  remove: []
}>()

function patch(changes: Partial<RuleGroup>) {
  emit('update:group', { ...props.group, ...changes })
}

function updateChild(index: number, node: RuleNode) {
  const conditions = [...props.group.conditions]
  conditions[index] = node
  patch({ conditions })
}

function removeChild(index: number) {
  patch({ conditions: props.group.conditions.filter((_, i) => i !== index) })
}

function addCondition() {
  patch({ conditions: [...props.group.conditions, emptyCondition()] })
}

function addGroup() {
  patch({ conditions: [...props.group.conditions, emptyGroup()] })
}
</script>

<template>
  <div :class="['space-y-2 rounded-md', !isRoot && 'border-primary/30 border-l-2 py-2 pl-4']">
    <div v-if="!hideMatchHeader" class="flex items-center gap-2">
      <span class="text-muted-foreground text-xs font-medium">Match</span>
      <Select :model-value="group.combinator" @update:model-value="(v) => patch({ combinator: v as 'and' | 'or' })">
        <SelectTrigger class="h-7 w-28 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="and">all (AND)</SelectItem>
          <SelectItem value="or">any (OR)</SelectItem>
        </SelectContent>
      </Select>
      <span class="text-muted-foreground text-xs font-medium">of the following:</span>

      <button
        v-if="!isRoot"
        type="button"
        class="text-muted-foreground hover:text-destructive ml-auto rounded p-1"
        aria-label="Remove group"
        @click="emit('remove')"
      >
        <Trash2 class="size-3.5" />
      </button>
    </div>

    <div v-if="group.conditions.length === 0" class="text-muted-foreground text-sm italic">
      No conditions yet — add one below.
    </div>

    <template v-for="(child, index) in group.conditions" :key="child.id">
      <ReportRuleCondition
        v-if="child.type === 'condition'"
        :condition="child as RuleCondition"
        :entity="entity"
        @update:condition="(v) => updateChild(index, v)"
        @remove="removeChild(index)"
      />
      <ReportRuleGroup
        v-else
        :group="child as RuleGroup"
        :entity="entity"
        @update:group="(v) => updateChild(index, v)"
        @remove="removeChild(index)"
      />
    </template>

    <div class="flex gap-2">
      <Button type="button" variant="outline" size="sm" @click="addCondition">
        <Plus class="size-3.5" />
        Condition
      </Button>
      <Button type="button" variant="outline" size="sm" @click="addGroup">
        <Plus class="size-3.5" />
        Group
      </Button>
    </div>
  </div>
</template>
